import { useRef, useEffect } from 'react';
import type { Variants, Transition } from 'framer-motion';

// ── Spring presets ──────────────────────────────────────────────────────
export const springs = {
  gentle: { type: 'spring', stiffness: 120, damping: 14 } as Transition,
  snappy: { type: 'spring', stiffness: 300, damping: 20 } as Transition,
  bouncy: { type: 'spring', stiffness: 400, damping: 10 } as Transition,
  soft: { type: 'spring', stiffness: 150, damping: 20 } as Transition,
  decorative: { type: 'spring', stiffness: 100, damping: 18 } as Transition,
  smooth: { type: 'spring', stiffness: 80, damping: 20 } as Transition,
  quick: { type: 'spring', stiffness: 500, damping: 25 } as Transition,
  layout: { type: 'spring', stiffness: 200, damping: 25 } as Transition,
};

// ── Panel slide variants ────────────────────────────────────────────────
export const panelSlideLeft: Variants = {
  hidden: { x: -20, opacity: 0 },
  visible: { x: 0, opacity: 1 },
  exit: { x: -20, opacity: 0 },
};

// ── Collapse/expand variants ────────────────────────────────────────────
export const collapseVariants: Variants = {
  collapsed: { height: 0, opacity: 0, overflow: 'hidden' },
  expanded: { height: 'auto', opacity: 1, overflow: 'hidden' },
};

// ── Drag feedback constants ─────────────────────────────────────────────
export const dragFeedback = {
  grabScale: 1.03,
  overlayOpacity: 0.85,
  rotation: -2,
  shadow: '0 12px 24px -4px rgba(0,0,0,0.15)',
};

// ── Modal entrance variants ─────────────────────────────────────────────
export const modalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
};

export const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

// ── Slide transition variants (for AnimatePresence between slides) ─────
export const slideTransitionVariants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
    scale: 0.98,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0,
    scale: 0.98,
  }),
};

/**
 * Bridge / scenario-group transition. Pure fade — no x-translate, no scale
 * tick. Used inside scenario groups to telegraph "still the same experience"
 * rather than the regular "next slide" beat. Slightly longer duration so it
 * reads as a deliberate pause, not a regression.
 */
export const scenarioGroupTransitionVariants: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

// ── Per-element entrance variants ──────────────────────────────────────
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 },
};

export const slideFromLeft: Variants = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0 },
};

export const slideFromRight: Variants = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0 },
};

export const slideFromBottom: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

// ── Accent line reveals ────────────────────────────────────────────────
export const lineReveal: Variants = {
  hidden: { scaleX: 0, originX: 0 },
  visible: { scaleX: 1 },
};

export const lineRevealCenter: Variants = {
  hidden: { scaleX: 0 },
  visible: { scaleX: 1 },
};

// ── Image reveal (subtle Ken Burns settle) ─────────────────────────────
export const imageReveal: Variants = {
  hidden: { opacity: 0, scale: 1.05 },
  visible: { opacity: 1, scale: 1 },
};

// ── Code reveal (opacity + y + blur) ───────────────────────────────────
export const codeReveal: Variants = {
  hidden: { opacity: 0, y: 15, filter: 'blur(4px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' },
};

// ── Stagger factory ────────────────────────────────────────────────────
export function staggerContainer(staggerDelay = 0.08): Variants {
  return {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
      },
    },
  };
}

export const staggerChild: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springs.gentle as object,
  },
};

// ── Interaction feedback ───────────────────────────────────────────────

/** Shake animation keyframes for incorrect answers */
export const shakeKeyframes = {
  x: [0, -10, 10, -10, 10, -5, 5, 0],
  transition: { duration: 0.5 },
};

/** Scale pulse for correct answers */
export const correctPulse = {
  scale: [1, 1.05, 1],
  transition: { duration: 0.3 },
};

// ── Direction hook for slide transitions ───────────────────────────────

/**
 * Tracks slide index changes and returns the direction of navigation.
 * Returns 1 for forward, -1 for backward.
 * Computed synchronously (no useState) to avoid extra re-renders that
 * disrupt AnimatePresence mid-animation.
 */
export function useSlideDirection(currentIndex: number): number {
  const prevIndexRef = useRef(currentIndex);

  const direction = currentIndex > prevIndexRef.current
    ? 1
    : currentIndex < prevIndexRef.current
    ? -1
    : 1; // default forward when equal (no navigation)

  useEffect(() => {
    prevIndexRef.current = currentIndex;
  }, [currentIndex]);

  return direction;
}
